import React from "react";
import izenuAPI from '../../services/izenuAPI';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';
// A paleta anterior repetia-se: o índigo do 1.º slot e o azul do 5.º eram quase
// iguais, tal como o esmeralda do 4.º e o turquesa do 8.º — com oito fatias, a
// legenda tornava-se impossível de casar com o gráfico.
//
// Esta ordem está validada para daltonismo (pior par adjacente ΔE 9.1 protan,
// 19.6 visão normal). Três das cores ficam abaixo de 3:1 de contraste com o
// fundo, o que obriga a rótulos visíveis — daí os valores irem na legenda.
const COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

// Séries únicas (um país = uma barra, a medida é sempre a mesma) usam uma só cor:
// oito cores aqui não codificariam informação nenhuma.
const BAR_COLOR = '#2a78d6';
// Fixed order so a platform with fewer origins still lines up column for column.
const SOURCE_ORDER = ['search', 'library', 'profile', 'playlist', 'discovery', 'radio', 'web', 'other'];
const PLATFORM_ORDER = ['web', 'ios', 'android', 'desconhecido'];
const PLATFORM_LABEL = { web: 'Web', ios: 'iOS', android: 'Android', desconhecido: 'Antigo / sem registo' };
const SOURCE_COLORS = { web: '#6366f1', search: '#f43f5e', library: '#10b981', playlist: '#f59e0b', radio: '#8b5cf6', profile: '#3b82f6', discovery: '#ec4899', other: '#94a3b8' };

function fmt(n) {
  if (!n) return '0';
  const num = Number(n);
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString('pt-PT');
}

function getCoverUrl(artistId, cover) {
  if (!cover) return null;
  if (cover.startsWith('http')) return cover;
  if (cover.startsWith('release_')) return `${CDN}/users/${artistId}/releases/${cover}`;
  return `${CDN}/songs/artworks/${cover}`;
}

function getPhoto(userId, photo) {
  if (!photo) return null;
  if (photo.startsWith('http')) return photo;
  return `${CDN}/users/${userId}/${photo}`;
}

const REASON_LABEL = {
  duplicate_60s: "duplicados",
  radio_as_music: "rádio como música",
  orphan_music: "música apagada",
};

function StatCard({ label, value, sub, color = '#6366f1', icon }) {
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statIcon, backgroundColor: color + '15', color }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <p style={s.statLabel}>{label}</p>
        <h2 style={{ ...s.statValue, color }}>{value}</h2>
        {sub && <p style={s.statSub}>{sub}</p>}
      </div>
    </div>
  );
}

export default function DashboardStreams() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data: r } = await izenuAPI.get('/admin/dashboard/fetchDashboardStreamData.php');
        setData(r);
      } catch (err) { console.log(err); }
    })();
  }, []);

  if (!data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>A carregar streams...</div>;
  }

  const countries = (data.streams_by_country || [])
    // Idem para 'unknown' — resíduo dos trinta minutos de Abril em que o ipapi.co
    // bloqueou o servidor. Escondê-lo faz o painel parecer mais completo do que é.
    .map(c => ({ ...c, country: c.country && c.country !== 'unknown' ? c.country : 'Desconhecido' }))
    .sort((a, b) => Number(b.total) - Number(a.total));

  // Faixas por etiquetar eram filtradas em silêncio — 112 streams, 2,4% do que é
  // ouvido, a desaparecer do painel sem deixar rasto. Num painel de administração
  // isso é informação accionável, não sujidade: passa a aparecer com nome próprio.
  const genres = (data.streams_by_genre || [])
    .map(g => ({ ...g, genre: g.genre && g.genre !== '' ? g.genre : 'Sem género' }))
    .sort((a, b) => Number(b.streams) - Number(a.streams));

  const sources = data.streams_by_source || [];

  // Genre doughnut
  const genreData = {
    labels: genres.slice(0, 8).map(g => g.genre),
    datasets: [{ data: genres.slice(0, 8).map(g => Number(g.streams)), backgroundColor: COLORS, borderWidth: 0, hoverOffset: 6 }]
  };
  // A legenda carrega o valor de cada fatia. Sem isso o donut não mostrava número
  // nenhum, a lista por baixo mostrava, e o primeiro número visível no ecrã era o
  // do 9.º lugar — que parecia o maior de todos.
  const doughnutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12, padding: 9, font: { size: 11 },
          generateLabels: (chart) => {
            const ds = chart.data.datasets[0];
            return chart.data.labels.map((label, i) => ({
              text: `${label} · ${fmt(ds.data[i])}`,
              fillStyle: ds.backgroundColor[i],
              strokeStyle: ds.backgroundColor[i],
              lineWidth: 0,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
            return ` ${ctx.label}: ${fmt(ctx.parsed)} (${pct}% do top 8)`;
          },
        },
      },
    },
  };

  // Country bar (top 10)
  const countryData = {
    labels: countries.slice(0, 10).map(c => c.country),
    datasets: [{ data: countries.slice(0, 10).map(c => Number(c.total)), backgroundColor: BAR_COLOR, borderRadius: 4, barThickness: 20 }]
  };
  const barOpts = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#888' } }, y: { grid: { display: false }, ticks: { font: { size: 12 }, color: '#333' } } }
  };

  // Source doughnut
  const sourceData = {
    labels: sources.map(s => s.source),
    datasets: [{ data: sources.map(s => Number(s.streams)), backgroundColor: sources.map(s => SOURCE_COLORS[s.source] || '#94a3b8'), borderWidth: 0, hoverOffset: 6 }]
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dashboard Streams</h1>
          <p style={s.subtitle}>Análise de streaming da plataforma</p>
        </div>
      </div>

      {/* Stats Row 1 */}
      <div style={s.row}>
        <StatCard label="Total Streams" value={fmt(data.total_streams)} sub="apenas streams válidos" color="#6366f1" icon="▶" />
        {/* O que foi registado mas não conta: rádio gravada como música, faixas
            apagadas, registos duplicados. Ver /stream-integrity. */}
        <StatCard label="Descartados" value={fmt(data.discarded_streams)}
          sub={(data.discarded_by_reason || []).map(r => `${REASON_LABEL[r.invalid_reason] || r.invalid_reason}: ${r.n}`).join(" · ") || "nenhum"}
          color="#f43f5e" icon="✕" />
        <StatCard label="Total Plays" value={fmt(data.total_plays)} sub="todas as reproduções" color="#f43f5e" icon="🔊" />
        <StatCard label="Ouvintes únicos" value={fmt(data.unique_listeners)} color="#10b981" icon="👥" />
        <StatCard label="Músicas ouvidas" value={fmt(data.unique_songs)} sub="músicas distintas" color="#f59e0b" icon="🎵" />
      </div>

      {/* Stats Row 2 */}
      <div style={s.row}>
        <StatCard label="Últimos 7 dias" value={fmt(data.streams_7d)} sub="streams" color="#8b5cf6" icon="📅" />
        <StatCard label="Últimos 30 dias" value={fmt(data.streams_30d)} sub="streams" color="#3b82f6" icon="📆" />
      </div>

      {/* Charts: Genre + Country */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Streams por Género</h3>
          <p style={s.cardDesc}>Géneros musicais mais ouvidos. Contabiliza apenas streams qualificados (reprodução mínima de 30 segundos).</p>
          {genres.length > 0 ? (
            <div style={{ height: 260 }}><Doughnut data={genreData} options={doughnutOpts} /></div>
          ) : <p style={s.empty}>Sem dados</p>}
          {/* A lista continua o gráfico a partir do 9.º lugar. Sem o título e sem a
              numeração, a sua primeira linha lia-se como o líder do painel. */}
          {genres.length > 8 && (
            <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
              <p style={s.listHeading}>Restantes géneros (9.º em diante)</p>
              {genres.slice(8).map((g, i) => (
                <div key={i} style={s.genreRow}>
                  <span style={s.genreName}><span style={s.rankNum}>{i + 9}.</span> {g.genre}</span>
                  <span style={s.genreCount}>{fmt(g.streams)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Streams por País</h3>
          <p style={s.cardDesc}>Distribuição geográfica dos streams, obtida via geolocalização do IP no momento da reprodução.</p>
          {countries.length > 0 ? (
            <div style={{ height: 260 }}><Bar data={countryData} options={barOpts} /></div>
          ) : <p style={s.empty}>Sem dados</p>}
          {/* Idem: continua o gráfico, não recomeça. */}
          {countries.length > 10 && (
            <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
              <p style={s.listHeading}>Restantes países (11.º em diante)</p>
              {countries.slice(10).map((c, i) => (
                <div key={i} style={s.genreRow}>
                  <span style={s.genreName}><span style={s.rankNum}>{i + 11}.</span> {c.country}</span>
                  <span style={s.genreCount}>{fmt(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Source chart */}
      {sources.length > 0 && (
        <div style={s.row}>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Streams por Origem</h3>
            <p style={s.cardDesc}>De onde o ouvinte iniciou a reprodução: busca, biblioteca, perfil do artista, playlist, rádio ou web.</p>
            <div style={{ height: 220 }}><Doughnut data={sourceData} options={doughnutOpts} /></div>
          </div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Detalhe por Origem</h3>
            <p style={s.cardDesc}>Peso de cada origem no total de streams, com percentagem e barra proporcional.</p>
            {sources.map((src, i) => {
              const pct = data.total_streams > 0 ? ((Number(src.streams) / data.total_streams) * 100).toFixed(1) : 0;
              return (
                <div key={i} style={s.sourceRow}>
                  <div style={{ ...s.sourceDot, backgroundColor: SOURCE_COLORS[src.source] || '#94a3b8' }} />
                  <span style={s.sourceName}>{src.source}</span>
                  <div style={s.sourceBar}>
                    <div style={{ ...s.sourceBarFill, width: `${pct}%`, backgroundColor: SOURCE_COLORS[src.source] || '#94a3b8' }} />
                  </div>
                  <span style={s.sourceCount}>{fmt(src.streams)}</span>
                  <span style={s.sourcePct}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Origin crossed with platform.
          Read apart, the two mislead: "other" is the biggest slice on the
          platform and holds three unrelated things — web streams whose origin
          was never tracked, mobile streams that genuinely had none, and
          everything from before device_info existed. */}
      {(data.streams_by_platform_source || []).length > 0 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Origem por Plataforma</h3>
          <p style={s.cardDesc}>
            A mesma origem cruzada com o dispositivo. O mobile segue sete origens reais;
            o site só começou a segui-las a 11/09/2026, por isso o histórico web aparece
            quase todo como <strong>other</strong> e vai encolher à medida que chegam streams novos.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.xtab}>
              <thead>
                <tr>
                  <th style={s.xth}>Plataforma</th>
                  {SOURCE_ORDER.map(src => <th key={src} style={s.xthNum}>{src}</th>)}
                  <th style={s.xthNum}>Total</th>
                </tr>
              </thead>
              <tbody>
                {PLATFORM_ORDER.map(plat => {
                  const rows = (data.streams_by_platform_source || []).filter(r => r.platform === plat);
                  if (rows.length === 0) return null;
                  const bySource = Object.fromEntries(rows.map(r => [r.source, Number(r.streams)]));
                  const total = rows.reduce((a, r) => a + Number(r.streams), 0);
                  return (
                    <tr key={plat} style={s.tr}>
                      <td style={s.xtd}><strong>{PLATFORM_LABEL[plat] || plat}</strong></td>
                      {SOURCE_ORDER.map(src => (
                        <td key={src} style={{ ...s.xtdNum, color: bySource[src] ? '#374151' : '#d8d8d8' }}>
                          {bySource[src] ? fmt(bySource[src]) : '—'}
                        </td>
                      ))}
                      <td style={{ ...s.xtdNum, fontWeight: 700 }}>{fmt(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Songs + Top Artists */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Top 10 Músicas</h3>
          <p style={s.cardDesc}>
            Ordenado por <strong>ouvinte-semanas</strong> nos últimos 90 dias — cada pessoa vale no
            máximo um ponto por semana, por muitas vezes que repita. O número cinzento é o total
            bruto de streams: quando os dois se afastam muito, a diferença é repetição, não audiência.
          </p>
          {(data.top_songs || []).map((song, i) => (
            <div key={song.music_id} style={s.listItem}>
              <span style={s.rank}>#{i + 1}</span>
              <img src={getCoverUrl(song.artist_id, song.cover)} alt="" style={s.coverImg} onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.itemTitle}>{song.title}</p>
                <p style={s.itemSub}>
                  {song.artist_name}{song.genre ? ` · ${song.genre}` : ''}
                  {song.listeners != null ? ` · ${fmt(song.listeners)} ouvintes` : ''}
                </p>
              </div>
              <div style={s.badgeCol}>
                <span style={s.streamBadge}>{fmt(song.streams)}</span>
                {song.raw_streams != null && <span style={s.rawBadge}>{fmt(song.raw_streams)} brutos</span>}
              </div>
            </div>
          ))}
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Top 10 Artistas</h3>
          <p style={s.cardDesc}>
            Mesma regra das músicas, sobre todo o catálogo do artista. Auto-streams não contam.
            Para investigar um caso, ver <a href="/stream-integrity" style={s.inlineLink}>Integridade de Streams</a>.
          </p>
          {(data.top_artists || []).map((a, i) => (
            <div key={a.user_id} style={s.listItem}>
              <span style={s.rank}>#{i + 1}</span>
              <img src={getPhoto(a.user_id, a.photo)} alt="" style={s.artistImg} onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.itemTitle}>{a.artist_name}</p>
                <p style={s.itemSub}>
                  {fmt(a.listeners || 0)} ouvintes · {a.songs_streamed || 0} músicas · {a.number_of_followers || 0} seguidores
                </p>
              </div>
              <div style={s.badgeCol}>
                <span style={s.streamBadge}>{fmt(a.streams)}</span>
                {a.raw_streams != null && <span style={s.rawBadge}>{fmt(a.raw_streams)} brutos</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const s = {
  container: { padding: '0 20px 40px 20px', maxWidth: 1300 },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { margin: '4px 0 0 0', fontSize: 13, color: '#999', fontWeight: 400 },
  row: { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  statCard: {
    flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: 14,
    padding: '18px 20px', backgroundColor: '#fff', borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', minWidth: 180,
  },
  statIcon: {
    width: 42, height: 42, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0,
  },
  statLabel: { margin: 0, fontSize: 12, color: '#888', fontWeight: 500 },
  statValue: { margin: '2px 0', fontSize: 24, fontWeight: 700, lineHeight: 1.2 },
  statSub: { margin: 0, fontSize: 11, color: '#aaa' },
  card: {
    flex: '1 1 400px', padding: 24, backgroundColor: '#fff', borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', minWidth: 320,
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#333', margin: '0 0 4px 0' },
  cardDesc: { margin: '0 0 14px 0', fontSize: 12, color: '#aaa', fontWeight: 400, lineHeight: 1.4 },
  listItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' },
  rank: { fontSize: 13, fontWeight: 700, color: '#bbb', width: 26, textAlign: 'center', flexShrink: 0 },
  coverImg: { width: 38, height: 38, borderRadius: 6, objectFit: 'cover', backgroundColor: '#f0f0f0', flexShrink: 0 },
  artistImg: { width: 38, height: 38, borderRadius: 19, objectFit: 'cover', backgroundColor: '#f0f0f0', flexShrink: 0 },
  itemTitle: { margin: 0, fontSize: 13, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemSub: { margin: 0, fontSize: 11, color: '#999' },
  badgeCol: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 },
  rawBadge: { fontSize: 10, color: '#b0b0b0', fontWeight: 500, whiteSpace: 'nowrap' },
  inlineLink: { color: '#6366f1', textDecoration: 'none', fontWeight: 600 },
  streamBadge: { fontSize: 13, fontWeight: 600, color: '#6366f1', flexShrink: 0 },
  empty: { color: '#ccc', textAlign: 'center', padding: 40, fontSize: 14 },
  listHeading: { margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3 },
  rankNum: { color: '#c4c4c4', fontWeight: 600, marginRight: 2 },
  xtab: { width: '100%', borderCollapse: 'collapse', minWidth: 640 },
  xth: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  xthNum: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  xtd: { padding: '8px 6px', fontSize: 13, color: '#374151' },
  xtdNum: { padding: '8px 6px', fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' },
  genreRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #fafafa' },
  genreName: { fontSize: 13, color: '#555' },
  genreCount: { fontSize: 13, fontWeight: 600, color: '#6366f1' },
  sourceRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' },
  sourceDot: { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  sourceName: { fontSize: 13, fontWeight: 500, color: '#333', width: 80, flexShrink: 0 },
  sourceBar: { flex: 1, height: 8, backgroundColor: '#f3f4f6', borderRadius: 4, overflow: 'hidden' },
  sourceBarFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  sourceCount: { fontSize: 13, fontWeight: 600, color: '#333', width: 50, textAlign: 'right', flexShrink: 0 },
  sourcePct: { fontSize: 11, color: '#999', width: 40, textAlign: 'right', flexShrink: 0 },
};
